import React, { useState, useEffect, useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Input, FormGroup, Row, Col, CardTitle } from 'reactstrap';
import {
   CenteredMsgShowing,
   ChartWrapper,
   HeightSetContainer,
   SimpleFlexItem,
   SimpleFlexRow,
   TwoSideFlexContainer,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetFooterContainer,
   WidgetHeaderContainer,
} from '../../../../styledComponents/styledComponents';

import { CsvExportFromApiButton, Spinner } from '../../../common';
import { useDispatch, useSelector } from 'react-redux';
import {
   cancelTokenSelector,
   endTimespanSelector,
   hashSelector,
   startTimespanSelector,
   streamNamesSelector,
   tagsSelector,
   titleTimespanSelector,
} from '../../../../redux/selectors/selectors';
import { getFilterQueryParams, getHttpHeader } from '../../../../util/FetchTools';
import useDataFetch from '../../../../hooks/useDataFetch';
import { setAlarm } from '../../../../redux/actions/general';
import { getIntegerWithDecimalSeparator, addSeparatorAndThreeDecimalPlaces } from '../../../../util/NumberFormatter';
import TrafficHint from '../../../common/Hints/TrafficHint';
import { LIVE_CAPTIONS_VIEW } from '../../../../constants/general';
import ErrorDisplay from '../../misuse/ErrorDisplay';

const paginationOptions = {
   sizePerPage: 10,
   sizePerPageList: [
      { text: '5', value: 5 },
      { text: '10', value: 10 },
      { text: '15', value: 15 },
      { text: '20', value: 20 },
      { text: '25', value: 25 },
   ],
   firstPageText: '<<',
   prePageText: '<',
   nextPageText: '>',
   lastPageText: '>>',
   showTotal: true,
};

const columnStyle = {
   style: { verticalAlign: 'middle', textAlign: 'right' },
   headerAlign: 'right',
};

const filterByStreamName = (rows, searchTerm) => {
   if (!searchTerm) return rows;
   const term = searchTerm.toLowerCase();
   return rows.filter((row) => (row.stream_name || '').toLowerCase().includes(term));
};

const STREAM_USAGE_CONFIG = {
   token: {
      title: 'Tokens Usage for Translation per Streamname',
      apiPath: '/v2/captions/usage/stream/token',
      csvFilename: 'TokenUsage_ByStream',
      footerLabel: 'Total tokens:',
      tooltipText:
         '<div>This table shows token usage per stream name.</div>' +
         '<div>Tokens measure the amount of speech processed during a translation session.</div>' +
         '<div>More processed speech results in higher token usage.</div>' +
         '<div>Total usage is recorded when the session ends,</div>' +
         '<div>so partial usage is not displayed in real time.</div>',
      columns: [
         {
            text: 'Stream',
            dataField: 'stream_name',
            sort: true,
            ...columnStyle,
         },
         {
            text: 'Tokens used',
            dataField: 'openai_num_tokens',
            sort: true,
            ...columnStyle,
            formatter: (cell) => (Number(cell) || 0).toLocaleString(),
         },
      ],
      processRawData: (rawData) =>
         rawData.map((item) => ({
            stream_name: item.stream,
            openai_num_tokens: item.token?.count ?? 0,
         })),
      getFooterValue: (rows) =>
         rows.reduce((sum, item) => sum + (Number(item.openai_num_tokens) || 0), 0).toLocaleString(),
   },
   uptime: {
      title: 'Uptime Usage per Streamname',
      apiPath: '/v2/captions/usage/stream/uptime',
      csvFilename: 'UptimeUsage_ByStream',
      footerLabel: 'Found streams:',
      tooltipText:
         '<div>This table shows uptime duration per stream name.</div>' +
         '<div>Usage is measured from the moment the transcription</div>' +
         '<div>service starts until it ends. The total usage is logged</div>' +
         '<div>when the session ends, so you will not see any</div>' +
         '<div>partial usage before this time.</div>',
      columns: [
         {
            text: 'Stream',
            dataField: 'stream_name',
            sort: true,
            ...columnStyle,
         },
         {
            text: 'Duration (Hours)',
            dataField: 'uptime_hours',
            sort: true,
            ...columnStyle,
            formatter: addSeparatorAndThreeDecimalPlaces,
         },
      ],
      processRawData: (rawData) => {
         const map = rawData.reduce((acc, item) => {
            const stream_name = item.stream;
            if (!acc[stream_name]) {
               acc[stream_name] = { stream_name, uptime_hours: 0 };
            }
            acc[stream_name].uptime_hours += (item.uptime?.seconds ?? 0) / 3600;
            return acc;
         }, {});
         return Object.values(map);
      },
      getFooterValue: (rows) => getIntegerWithDecimalSeparator(rows.length),
   },
};

function UsageByStreamName({
   title,
   apiPath,
   csvFilename,
   columns,
   tooltipText,
   processRawData,
   footerLabel,
   getFooterValue,
}) {
   const timespanTitle = useSelector(titleTimespanSelector);
   const start = useSelector(startTimespanSelector);
   const end = useSelector(endTimespanSelector);
   const selectedHash = useSelector(hashSelector);
   const cancelToken = useSelector(cancelTokenSelector);
   const tags = useSelector(tagsSelector);
   const streamNames = useSelector(streamNamesSelector);

   const dispatch = useDispatch();

   const [tableData, setTableData] = useState([]);
   const [noData, setNoData] = useState(false);
   const [searchTerm, setSearchTerm] = useState('');

   const header = useMemo(() => getHttpHeader(selectedHash), [selectedHash]);
   const urlFilterQuery = getFilterQueryParams([], tags, [], streamNames, LIVE_CAPTIONS_VIEW);

   const url = `${apiPath}?from=${start.format()}&to=${end.format()}${urlFilterQuery}`;

   const { successful, isLoading, errorMessage, data } = useDataFetch(url, {
      header,
      cancelToken,
   });

   useEffect(() => {
      if (successful) {
         setNoData(data.statusCode === 2000);
         const rawData = data.data || [];
         setTableData(processRawData(rawData));
      }

      if (errorMessage) {
         dispatch(setAlarm('danger', errorMessage));
      }
   }, [data, successful, errorMessage, dispatch, processRawData]);

   const aggregatedData = useMemo(() => filterByStreamName(tableData, searchTerm), [tableData, searchTerm]);

   const handleSearchChange = (e) => {
      setSearchTerm(e.target.value);
   };

   const errorOrNoData = errorMessage || noData;

   return (
      <WidgetContainer>
         <WidgetHeaderContainer>
            <HeightSetContainer $heightInPx={23}>
               <TwoSideFlexContainer>
                  <SimpleFlexRow>
                     <CsvExportFromApiButton
                        url={url}
                        header={header}
                        cancelToken={cancelToken}
                        filename={csvFilename}
                     />
                  </SimpleFlexRow>
                  <SimpleFlexRow>
                     <SimpleFlexItem>
                        <TrafficHint sizePx={16} customTip={tooltipText} />
                     </SimpleFlexItem>
                  </SimpleFlexRow>
               </TwoSideFlexContainer>
            </HeightSetContainer>
         </WidgetHeaderContainer>
         <WidgetBodyContainer $minHeight={'680px'}>
            <Row className='mb-3'>
               <Col>
                  <CardTitle className='mb-0'>{title}</CardTitle>
                  <div className='small text-muted'>{`${timespanTitle}\u00A0\u00A0\u00A0( UTC time )`}</div>
               </Col>
            </Row>
            <ChartWrapper className='chart-wrapper' marginTopPx={15}>
               <Spinner loading={isLoading} parentTopMarginPx={-250}>
                  {errorOrNoData ? (
                     <CenteredMsgShowing height={602} marginTopPx={0}>
                        {errorMessage ? (
                           <ErrorDisplay error={errorMessage} msgColor={'rgb(80,80,80)'}></ErrorDisplay>
                        ) : (
                           'No data available'
                        )}
                     </CenteredMsgShowing>
                  ) : (
                     <>
                        <TwoSideFlexContainer>
                           <div />
                           <FormGroup style={{ width: '250px' }}>
                              <Input
                                 type='text'
                                 id='streamSearch'
                                 placeholder='Search by stream name'
                                 value={searchTerm}
                                 onChange={handleSearchChange}
                              />
                           </FormGroup>
                        </TwoSideFlexContainer>
                        <BootstrapTable
                           keyField='stream_name'
                           data={aggregatedData}
                           columns={columns}
                           striped
                           hover
                           pagination={paginationFactory(paginationOptions)}
                           noDataIndication='No stream data available'
                           classes='table-responsive-sm'
                           bootstrap4
                        />
                     </>
                  )}
               </Spinner>
            </ChartWrapper>
         </WidgetBodyContainer>
         <WidgetFooterContainer>
            <Row>
               <Col className='text-right'>{footerLabel}</Col>
               <Col>{isLoading || errorOrNoData ? '0' : getFooterValue(aggregatedData)}</Col>
            </Row>
         </WidgetFooterContainer>
      </WidgetContainer>
   );
}

export function TokenUsageByStreamName() {
   return <UsageByStreamName {...STREAM_USAGE_CONFIG.token} />;
}

export function UptimeUsageByStreamName() {
   return <UsageByStreamName {...STREAM_USAGE_CONFIG.uptime} />;
}

export default UsageByStreamName;
